import dayjs from 'dayjs'
import type { IInvalidFieldsSwalError } from 'components/InvalidFieldsError/InvalidFieldsError'
import { IAddNewClientDocumentKey, IAddNewClientDocuments, IAddNewClientValues, IApiPartnerDocumentsPayload } from 'egi/app/Leads/leadInterfaces'
import { getBase64 } from 'utils/download'
import translateAntForm from 'utils/translateAntForm'

interface IAppendClientFormDataOptions {
  includeDocuments?: boolean
}

interface IClientSimulationDocumentsPayload {
  apiPartnerDocuments?: IApiPartnerDocumentsPayload
  currentStep: number
  simulationId?: string
}

interface IInvalidResponseData {
  invalid?: IInvalidFieldsSwalError['invalidFields']
  data?: {
    invalid?: IInvalidFieldsSwalError['invalidFields']
  }
}

interface IInvalidResponse {
  data?: IInvalidResponseData
}

function removeBase64Prefix (base64: string) {
  return base64.split(',')[1] || base64
}

function isInvalidResponse (err: unknown): err is IInvalidResponse {
  return typeof err === 'object' && err !== null && 'data' in err
}

async function getApiPartnerDocuments (documents?: IAddNewClientDocuments) {
  if (!documents) return undefined

  const apiPartnerDocuments: IApiPartnerDocumentsPayload = {}

  for (const documentKey of Object.keys(documents) as IAddNewClientDocumentKey[]) {
    const file = documents[documentKey]?.[0]?.originFileObj

    if (file) {
      const base64 = await getBase64(file)
      apiPartnerDocuments[documentKey] = removeBase64Prefix(base64)
    }
  }

  if (Object.keys(apiPartnerDocuments).length) return apiPartnerDocuments

  return undefined
}

async function appendApiPartnerDocuments (formData: FormData, documents?: IAddNewClientDocuments) {
  const apiPartnerDocuments = await getApiPartnerDocuments(documents)

  if (apiPartnerDocuments) formData.append('apiPartnerDocuments', JSON.stringify(apiPartnerDocuments))
}

function formatClientFormValue (value: IAddNewClientValues[keyof IAddNewClientValues]) {
  if (value === undefined || value === null) return undefined
  if (dayjs.isDayjs(value)) return value.format('YYYY-MM-DD')
  if (typeof value === 'object') return undefined

  return String(value)
}

export async function getClientSimulationDocumentsPayload (values: IAddNewClientValues, currentStep: number): Promise<IClientSimulationDocumentsPayload> {
  const apiPartnerDocuments = await getApiPartnerDocuments(values.documents)

  return {
    apiPartnerDocuments,
    currentStep,
    simulationId: values.simulationId
  }
}

export async function appendClientFormData (formData: FormData, values: IAddNewClientValues, options?: IAppendClientFormDataOptions) {
  if (options?.includeDocuments !== false) {
    await appendApiPartnerDocuments(formData, values.documents)
  }

  for (const item in values) {
    if (item === 'documents') continue

    const value = formatClientFormValue(values[item as keyof IAddNewClientValues])

    if (value !== undefined) formData.append(item, value)
  }
}

export function getInvalidFieldsFromClientFormError (err: unknown) {
  if (!isInvalidResponse(err)) return undefined

  return err.data?.invalid ?? err.data?.data?.invalid
}

export function formatClientFormInvalidFields (invalidFields: IInvalidFieldsSwalError['invalidFields']) {
  const translatedInvalidFields = translateAntForm.formatToSwalError(invalidFields)

  if (translatedInvalidFields.length) return translatedInvalidFields

  return invalidFields
}
